/**
 * Sample boards.
 *
 * Clearly labelled, badged in the board list, seeded only on a first run, and
 * removable with one button. Element ids are deterministic so the arrow
 * connections below are readable.
 */

import { DEFAULT_STYLE } from './elements';
import { DOC_VERSION, type ArrowElement, type Board, type BoxElement, type SlateElement, type TextElement } from './types';

const SAMPLE_CLIENT = 'sample-data';

interface BoxSpec {
  id: string;
  type: BoxElement['type'];
  x: number;
  y: number;
  width: number;
  height: number;
  label?: string;
  fill?: string;
  stroke?: string;
}

function base(id: string, createdAt: number) {
  return {
    id,
    angle: 0,
    strokeColor: DEFAULT_STYLE.strokeColor,
    backgroundColor: DEFAULT_STYLE.backgroundColor,
    fillStyle: 'solid' as const,
    strokeWidth: 2,
    strokeStyle: 'solid' as const,
    opacity: 1,
    version: 1,
    updatedAt: createdAt,
    updatedBy: SAMPLE_CLIENT,
    isDeleted: false,
  };
}

function makeBox(spec: BoxSpec, createdAt: number): BoxElement {
  return Object.freeze({
    ...base(spec.id, createdAt),
    type: spec.type,
    x: spec.x,
    y: spec.y,
    width: spec.width,
    height: spec.height,
    backgroundColor: spec.fill ?? DEFAULT_STYLE.backgroundColor,
    strokeColor: spec.stroke ?? DEFAULT_STYLE.strokeColor,
    fillStyle: (spec.fill === 'transparent' ? 'transparent' : 'solid') as 'solid' | 'transparent',
  });
}

function makeLabel(container: BoxSpec, text: string, createdAt: number): TextElement {
  return Object.freeze({
    ...base(`${container.id}-label`, createdAt),
    type: 'text' as const,
    x: container.x + 8,
    y: container.y + container.height / 2 - 10,
    width: Math.max(16, container.width - 16),
    height: 20,
    fillStyle: 'transparent' as const,
    text,
    fontSize: 15,
    fontFamily: 'sans' as const,
    textAlign: 'center' as const,
    containerId: container.id,
  });
}

function makeNote(id: string, x: number, y: number, text: string, createdAt: number): TextElement {
  return Object.freeze({
    ...base(id, createdAt),
    type: 'text' as const,
    x,
    y,
    width: 320,
    height: 20,
    fillStyle: 'transparent' as const,
    strokeColor: '#b54708',
    text,
    fontSize: 14,
    fontFamily: 'sans' as const,
    textAlign: 'left' as const,
    containerId: null,
  });
}

function makeArrow(
  id: string,
  from: BoxSpec,
  to: BoxSpec,
  createdAt: number,
): ArrowElement {
  const start = { x: from.x + from.width / 2, y: from.y + from.height / 2 };
  const end = { x: to.x + to.width / 2, y: to.y + to.height / 2 };
  return Object.freeze({
    ...base(id, createdAt),
    type: 'arrow' as const,
    x: start.x,
    y: start.y,
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
    fillStyle: 'transparent' as const,
    points: [
      [0, 0],
      [end.x - start.x, end.y - start.y],
    ] as ReadonlyArray<readonly [number, number]>,
    startBinding: { elementId: from.id, gap: 4 },
    endBinding: { elementId: to.id, gap: 4 },
    startArrowhead: false,
    endArrowhead: true,
  });
}

function sprintBoard(createdAt: number): Board {
  const backlog: BoxSpec = { id: 'sf-backlog', type: 'rect', x: 80, y: 120, width: 170, height: 90, label: 'Backlog', fill: '#dbeafe' };
  const doing: BoxSpec = { id: 'sf-doing', type: 'rect', x: 330, y: 120, width: 170, height: 90, label: 'In progress', fill: '#fef0c7' };
  const review: BoxSpec = { id: 'sf-review', type: 'rect', x: 580, y: 120, width: 170, height: 90, label: 'In review', fill: '#ece9fe' };
  const done: BoxSpec = { id: 'sf-done', type: 'ellipse', x: 830, y: 115, width: 180, height: 100, label: 'Shipped', fill: '#dcfae6' };
  const blocked: BoxSpec = { id: 'sf-blocked', type: 'diamond', x: 320, y: 300, width: 190, height: 120, label: 'Blocked?', fill: '#fee4e2' };

  const boxes = [backlog, doing, review, done, blocked];
  const elements: SlateElement[] = [];
  for (const spec of boxes) {
    elements.push(makeBox(spec, createdAt));
    if (spec.label) elements.push(makeLabel(spec, spec.label, createdAt));
  }
  elements.push(
    makeArrow('sf-a1', backlog, doing, createdAt),
    makeArrow('sf-a2', doing, review, createdAt),
    makeArrow('sf-a3', review, done, createdAt),
    makeArrow('sf-a4', doing, blocked, createdAt),
    makeArrow('sf-a5', blocked, backlog, createdAt),
    makeNote(
      'sf-note',
      80,
      52,
      'Sample board. Delete it any time from the board list.',
      createdAt,
    ),
  );

  return {
    id: 'sample-sprint-flow',
    name: 'Sample: Sprint planning flow',
    createdAt,
    updatedAt: createdAt,
    docVersion: DOC_VERSION,
    elements,
    view: { scrollX: -60, scrollY: 10, zoom: 1 },
    isSample: true,
  };
}

function systemBoard(createdAt: number): Board {
  const browser: BoxSpec = { id: 'ss-browser', type: 'rect', x: 100, y: 130, width: 190, height: 90, label: 'React editor', fill: '#dbeafe' };
  const reducer: BoxSpec = { id: 'ss-reducer', type: 'rect', x: 390, y: 130, width: 190, height: 90, label: 'Pure reducer', fill: '#ece9fe' };
  const idb: BoxSpec = { id: 'ss-idb', type: 'rect', x: 390, y: 320, width: 190, height: 90, label: 'IndexedDB', fill: '#dcfae6' };
  const exporters: BoxSpec = { id: 'ss-export', type: 'diamond', x: 670, y: 115, width: 200, height: 120, label: 'SVG / PNG', fill: '#fef0c7' };
  const tabs: BoxSpec = { id: 'ss-tabs', type: 'ellipse', x: 100, y: 320, width: 190, height: 90, label: 'Other tab', fill: '#fee4e2' };

  const boxes = [browser, reducer, idb, exporters, tabs];
  const elements: SlateElement[] = [];
  for (const spec of boxes) {
    elements.push(makeBox(spec, createdAt));
    if (spec.label) elements.push(makeLabel(spec, spec.label, createdAt));
  }

  const squiggle: SlateElement = Object.freeze({
    ...base('ss-squiggle', createdAt),
    type: 'freedraw' as const,
    x: 690,
    y: 300,
    width: 150,
    height: 60,
    fillStyle: 'transparent' as const,
    strokeColor: '#b54708',
    points: [
      [0, 30],
      [20, 6],
      [42, 52],
      [64, 8],
      [86, 50],
      [108, 12],
      [130, 40],
      [150, 22],
    ] as ReadonlyArray<readonly [number, number]>,
  });

  elements.push(
    makeArrow('ss-a1', browser, reducer, createdAt),
    makeArrow('ss-a2', reducer, idb, createdAt),
    makeArrow('ss-a3', reducer, exporters, createdAt),
    makeArrow('ss-a4', idb, tabs, createdAt),
    squiggle,
    makeNote('ss-note', 100, 62, 'Sample board. Safe to delete.', createdAt),
  );

  return {
    id: 'sample-system-sketch',
    name: 'Sample: System sketch',
    createdAt,
    updatedAt: createdAt,
    docVersion: DOC_VERSION,
    elements,
    view: { scrollX: -60, scrollY: 20, zoom: 1 },
    isSample: true,
  };
}

export function createSampleBoards(now = Date.now()): Board[] {
  return [sprintBoard(now), systemBoard(now)];
}

export const SAMPLE_BOARD_IDS = ['sample-sprint-flow', 'sample-system-sketch'] as const;
